import { useEffect, type FC, type RefObject } from 'react';

import { _notNil } from '@/littledash';
import type { MetadataField } from '@/model/Metadata.model';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';

import Spreadsheet from '@/components/UI/SpreadSheet/Spreadsheet';
import type { SpreadsheetSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import { timepointAfterPasteHandler } from '@/components/UI/SpreadSheet/editors/TimepointEditor';
import { defaultMetadataToColumnSettingMapper } from '@/support/hot';

interface BulkSampleEditTableProps {
  data: Array<Record<string, unknown>>;
  settings: SpreadsheetSettings;
  innerRef: RefObject<HotTableRef>;
  metadata: Array<MetadataField>;
  setMetadata: (metadata: Array<MetadataField>) => void;
  setInvalid: (invalid: boolean) => void;
  maxRows: number;
  customAfterRenderer?: (
    td: HTMLTableCellElement,
    row: number,
    col: number,
    prop: string | number,
    value: unknown,
    cellProperties: object
  ) => void;
}

const BulkSampleEditTable: FC<BulkSampleEditTableProps> = ({
  data,
  settings,
  innerRef,
  metadata,
  setMetadata,
  setInvalid,
  maxRows,
  customAfterRenderer,
}) => {
  useEffect(() => {
    const hot = innerRef.current?.hotInstance;
    if (_notNil(hot)) {
      hot.addHook('afterPaste', timepointAfterPasteHandler);

      return () => {
        try {
          if (!hot.isDestroyed) {
            hot.removeHook('afterPaste', timepointAfterPasteHandler);
          }
        } catch (error) {
          ExceptionHandler.captureException(
            new InVivoError('Failed to remove timepoint paste hook during cleanup', {
              cause: error,
              slug: 'bulk-sample-edit-hot-cleanup-error',
            })
          );
        }
      };
    }
  }, [innerRef]);

  return (
    <Spreadsheet
      className="pl4 pr4"
      testPrefix="bulk-sample-edit"
      data={data}
      innerRef={innerRef}
      settings={settings}
      metadata={metadata}
      setMetadata={setMetadata}
      setInvalid={setInvalid}
      metadataToColumnSettingMapper={defaultMetadataToColumnSettingMapper}
      maxRows={maxRows}
      greedyValidateRow
      customAfterRenderer={customAfterRenderer}
    />
  );
};

export default BulkSampleEditTable;
